import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { WalletProvider, useWallet } from './contexts/WalletContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import ErrorBoundary from './components/Common/ErrorBoundary';
import VersionChecker from './components/VersionChecker';
import { theme as brandTheme } from './styles/theme';
import './App.css';

// Layout Components
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ErrorPage from './components/ErrorPage';

// Pages - Lazy load to avoid loading recharts on HomePage
import HomePage from './components/Pages/HomePage';
const DashboardPage = React.lazy(() => import('./components/Pages/Dashboard'));
const ProjectsPage = React.lazy(() => import('./components/Pages/Projects'));
const ProjectEditorPage = React.lazy(() => import('./components/Pages/ProjectEditor'));
const ProjectBuilderPage = React.lazy(() => import('./components/Pages/ProjectBuilderPage'));
const ProjectSettingsPage = React.lazy(() => import('./components/Pages/ProjectSettings'));
const AnalyticsPage = React.lazy(() => import('./components/Pages/Analytics'));
const ContactUsPage = React.lazy(() => import('./components/Pages/ContactUs'));
const CustomNFTBuilderPage = React.lazy(() => import('./components/Pages/CustomNFTBuilder'));
const RarityAnalysisPage = React.lazy(() => import('./components/Pages/RarityAnalysisPage'));
const CurationPage = React.lazy(() => import('./components/Pages/CurationPage'));
const FreeGalleryPage = React.lazy(() => import('./components/Pages/FreeGalleryPage'));
const ReferralRedirect = React.lazy(() => import('./components/Pages/ReferralRedirect'));

// Mobile components removed - desktop UI is now fully responsive

// Create a theme instance - derives from centralized brand theme (styles/theme.js)
const bt = brandTheme.colors;
const muiTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: bt.primary,
      dark: bt.primaryDark,
      light: bt.primaryLight,
      contrastText: '#fef5f5',
    },
    secondary: {
      main: bt.surface800,
      dark: bt.surface900,
      light: bt.surface700,
      contrastText: '#fafafa',
    },
    background: {
      default: bt.surface950,
      paper: bt.surface900,
    },
    text: {
      primary: bt.textPrimary,
      secondary: bt.textSecondary,
    },
    error: {
      main: bt.error,
    },
    warning: {
      main: bt.warning,
    },
    success: {
      main: bt.success,
    },
    info: {
      main: bt.info,
    },
    divider: bt.borderSubtle,
  },
  typography: {
    fontFamily: brandTheme.typography.fontFamily.body,
    h1: {
      fontFamily: brandTheme.typography.fontFamily.heading,
      fontWeight: 800,
      fontSize: '2.5rem',
      letterSpacing: '-0.02em',
      textShadow: `0 0 8px ${bt.primaryAlpha30}`,
    },
    h2: {
      fontFamily: brandTheme.typography.fontFamily.heading,
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: brandTheme.typography.fontFamily.heading,
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: brandTheme.typography.fontFamily.heading,
      fontWeight: 700,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
    },
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1rem' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: brandTheme.gradients.bodyBg,
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          background: bt.primaryAlpha80,
          border: `1px solid ${bt.primaryAlpha30}`,
          '&:hover': {
            background: bt.primary,
            boxShadow: brandTheme.shadows.neonGlow,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
          '&:disabled': {
            background: bt.primaryAlpha20,
            color: 'rgba(254, 245, 245, 0.4)',
            border: `1px solid ${bt.primaryAlpha10}`,
          },
        },
        outlined: {
          borderColor: bt.surface700,
          color: bt.textSecondary,
          '&:hover': {
            borderColor: bt.surface600,
            background: 'rgba(255, 255, 255, 0.03)',
            color: bt.textPrimary,
          },
        },
        text: {
          color: bt.textSecondary,
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.05)',
            color: bt.textPrimary,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: bt.textMuted,
          transition: 'all 0.2s ease',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.05)',
            color: bt.textPrimary,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          backgroundImage: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: bt.surface900,
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: `${bt.surface950}f2`,
          backdropFilter: 'blur(24px)',
          borderRight: `1px solid ${bt.borderSubtle}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `${bt.surface950}bf`,
          backdropFilter: 'blur(24px)',
          borderBottom: `1px solid rgba(255, 255, 255, 0.04)`,
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: bt.surface800,
            borderRadius: 8,
            '& fieldset': {
              borderColor: bt.surface700,
            },
            '&:hover fieldset': {
              borderColor: bt.surface600,
            },
            '&.Mui-focused fieldset': {
              borderColor: bt.primary,
              boxShadow: `0 0 0 3px ${bt.primaryAlpha10}`,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          background: bt.surface800,
          '&:hover': {
            background: bt.surface750,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: `${bt.surface900}f5`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${bt.borderSubtle}`,
          borderRadius: 10,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          mx: 0.5,
          transition: 'all 0.15s ease',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.05)',
          },
          '&.Mui-selected': {
            background: bt.primaryAlpha10,
            '&:hover': {
              background: bt.primaryAlpha15,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          background: bt.surface800,
          border: `1px solid ${bt.borderSubtle}`,
          fontWeight: 500,
        },
        filled: {
          background: bt.primaryAlpha15,
          border: `1px solid ${bt.primaryAlpha20}`,
          color: bt.primaryLight,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          background: `linear-gradient(90deg, ${bt.surface800} 25%, ${bt.surface700} 50%, ${bt.surface800} 75%)`,
          backgroundSize: '200% 100%',
          animation: 'shimmer 2s infinite',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          background: bt.bgCard,
          backdropFilter: 'blur(12px)',
          borderRadius: 12,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            background: bt.surface800,
            borderBottom: `1px solid ${bt.borderSubtle}`,
            fontWeight: 600,
            color: bt.textSecondary,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background 0.15s ease',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.02)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${bt.borderSubtle}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: bt.borderSubtle,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          background: bt.surface800,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${bt.borderSubtle}`,
          borderRadius: 10,
        },
        standardError: {
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        },
        standardWarning: {
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
        },
        standardSuccess: {
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        },
        standardInfo: {
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: `${bt.surface800}f5`,
          backdropFilter: 'blur(12px)',
          border: `1px solid ${bt.borderSubtle}`,
          borderRadius: 8,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          background: bt.surface700,
          borderRadius: 4,
          height: 6,
        },
        bar: {
          background: `linear-gradient(90deg, ${bt.primary} 0%, ${bt.primaryLight} 100%)`,
          borderRadius: 4,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: bt.primary,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: bt.surface900,
          border: `1px solid ${bt.borderSubtle}`,
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            borderColor: bt.primaryAlpha20,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 2,
          borderRadius: '2px 2px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          transition: 'color 0.2s ease',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: bt.surface700,
        },
      },
    },
  },
});

// Main App component
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <VersionChecker />
        <UserPreferencesProvider>
          <WalletProvider>
            <Router>
              <SsoHandoff />
              <AppRoutes />
            </Router>
          </WalletProvider>
        </UserPreferencesProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Captures ?sso_token=... on first load, stores the JWT where lib/api.js
// expects it (`boneforge_jwt` in localStorage), strips the query params, and
// reloads so WalletProvider picks the token up. Used by the Gravemint
// creator admin's "Open in GraveForge" deep link — the deep link now puts
// the project id in the PATH (/app/projects/:id/layers), so this component
// just handles the JWT handoff; React Router renders the landing page. Works
// because both apps share the same SUPABASE_JWT_SECRET, so the token
// Gravemint issued is already valid here.
function SsoHandoff() {
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const token = url.searchParams.get('sso_token');
      if (!token) return;
      localStorage.setItem('boneforge_jwt', token);
      url.searchParams.delete('sso_token');
      url.searchParams.delete('from');
      // Legacy cleanup: an older deep-link shape put the project id in a
      // ?project= query param. Strip it if present so it doesn't linger.
      url.searchParams.delete('project');
      const cleanQuery = url.searchParams.toString();
      const nextUrl = url.pathname + (cleanQuery ? `?${cleanQuery}` : '') + (url.hash || '');
      window.history.replaceState({}, '', nextUrl);
      // Nudge a reload so WalletProvider / protected routes see the new token.
      window.location.reload();
    } catch (err) {
      console.error('[SsoHandoff] failed:', err);
    }
  }, []);
  return null;
}

// Separate component for routes that need wallet context
function AppRoutes() {
  const { connected } = useWallet();

  return (
    <React.Suspense fallback={
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: '24px',
        backgroundColor: '#000000'
      }}>
        <img 
          src="https://images.solanadeads.com/graveforge.png"
          alt="GraveForge Logo"
          style={{ 
            width: '200px',
            height: 'auto',
            animation: 'pulse 2s ease-in-out infinite'
          }}
        />
        <div style={{ 
          fontSize: '18px',
          color: '#a3a3a3',
          fontWeight: 500
        }}>
          Loading...
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    }>
      <Routes>
        {/* Public routes - no layout */}
        <Route path="/" element={<HomePage />} />
        
        {/* Protected routes - with layout */}
        <Route path="/app" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/new" element={<ProjectEditorPage isNew />} />
          
          {/* Project Builder - Dedicated Pages */}
          <Route path="projects/:projectId/settings" element={<ProjectSettingsPage />} />
          <Route path="projects/:projectId/layers" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/paths" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/path-analytics" element={<ProjectBuilderPage />} />
          {/* path-groups, probability-curves now accessible via Advanced section in Paths page */}
          <Route path="projects/:projectId/rules" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/rule-chains" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/trait-count" element={<ProjectBuilderPage />} />
          {/* Removed: conditional-paths, rule-templates, path-templates (dead/low-value features) */}
          <Route path="projects/:projectId/visual-path-testing" element={<ProjectBuilderPage />} />
          {/* Removed: path-inheritance, nested-paths (dead routes) */}
          {/* color-harmony, rules-info now embedded as accordions in Rules page */}
          <Route path="projects/:projectId/preview" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/custom" element={<CustomNFTBuilderPage />} />
          <Route path="projects/:projectId/rarity" element={<RarityAnalysisPage />} />
          <Route path="projects/:projectId/metadata" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/exclusions" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/generate" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/payments" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/analytics" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/security" element={<ProjectBuilderPage />} />
          
          {/* Team & Collaboration */}
          <Route path="projects/:projectId/team" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/notes" element={<ProjectBuilderPage />} />
          <Route path="projects/:projectId/activity" element={<ProjectBuilderPage />} />
          
          {/* Curated Generation - nested under projects */}
          <Route path="projects/:projectId/curated/:curatedJobId" element={<CurationPage />} />

          {/* Free Tier Gallery */}
          <Route path="projects/:projectId/free-gallery/:jobId" element={<FreeGalleryPage />} />
          
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="contact" element={<ContactUsPage />} />
        </Route>

        {/* Referral redirect */}
        <Route path="/r/:code" element={<ReferralRedirect />} />

        {/* Catch all */}
        <Route path="*" element={<ErrorPage code={404} />} />
    </Routes>
  </React.Suspense>
  );
}

export default App;
